import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { VideoCameraIcon as VideoCamera, ClockIcon as Clock, CoinsIcon as Coins } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils-app";

/**
 * Buyer-facing dialog to request a 15-minute live video tour with the
 * dog's owner. Posts to /api/video-tours/request which creates a
 * pending_seller row; the seller responds via their /tours dashboard.
 */
export default function BookVideoTourDialog({ open, onOpenChange, dogId, dogName }) {
  const today = new Date();
  // Default the picker to "tomorrow at noon" so the user gets a sane,
  // valid starting point (>30min in the future, <30 days out).
  const tomorrowNoon = new Date(today);
  tomorrowNoon.setDate(tomorrowNoon.getDate() + 1);
  tomorrowNoon.setHours(12, 0, 0, 0);
  const defaultLocal = (() => {
    const d = tomorrowNoon;
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();

  const [proposedAt, setProposedAt] = useState(defaultLocal);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  // Client-side validation matching server rules so the user gets
  // immediate feedback instead of an API error toast after a roundtrip.
  const validate = (iso) => {
    if (!iso) return "Pick a date and time.";
    const local = new Date(iso);
    if (Number.isNaN(local.getTime())) return "That date/time isn't valid.";
    const now = new Date();
    const minMs = 30 * 60 * 1000;        // 30 minutes
    const maxMs = 30 * 24 * 60 * 60 * 1000; // 30 days
    const diff = local.getTime() - now.getTime();
    if (diff < minMs) return "Pick a time at least 30 minutes from now.";
    if (diff > maxMs) return "Pick a time within the next 30 days.";
    return null;
  };
  const validationError = validate(proposedAt);

  const submit = async () => {
    const err = validate(proposedAt);
    if (err) {
      toast.error(err);
      return;
    }
    setBusy(true);
    try {
      // datetime-local fields don't carry timezone — interpret as the
      // user's local zone and convert to UTC ISO.
      const local = new Date(proposedAt);
      const r = await api.post("/video-tours/request", {
        dog_id: dogId,
        proposed_at: local.toISOString(),
        message: message.trim(),
      });
      toast.success(
        `Tour request sent! ${r.data.seller?.name || "The seller"} will accept or decline shortly.`
      );
      onOpenChange(false);
      setMessage("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="book-video-tour-dialog">
        <DialogHeader>
          <DialogTitle className="font-spectral text-2xl flex items-center gap-2">
            <VideoCamera size={22} weight="duotone" className="text-crimson" />
            Book a video tour
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            Walk through the kennel, meet {dogName ? <strong>{dogName}</strong> : "the dog"} and any siblings, and
            ask the breeder questions in real time — without flying out first.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 my-2">
          <div className="rounded-lg border border-[#E5D08A] bg-[#FBF7EC] p-3 text-[12.5px] text-[#5C4A00] flex flex-wrap gap-x-4 gap-y-1">
            <span className="flex items-center gap-1">
              <Clock size={13} weight="duotone" /> 15-minute call
            </span>
            <span className="flex items-center gap-1">
              <Coins size={13} weight="duotone" /> $35 booking fee
            </span>
            <span className="flex items-center gap-1">
              <VideoCamera size={13} weight="duotone" /> Browser-based, nothing to install
            </span>
          </div>

          <div>
            <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              When works for you?
            </Label>
            <Input
              type="datetime-local"
              value={proposedAt}
              onChange={(e) => setProposedAt(e.target.value)}
              className="mt-1 rounded-lg"
              data-testid="book-tour-time"
            />
            <p className="text-[11px] text-[#6B6B6B] mt-1.5">
              At least 30 min from now, no more than 30 days out. The seller can accept or counter.
            </p>
            {validationError && proposedAt && (
              <p className="text-[11.5px] text-crimson mt-1.5 font-bold" data-testid="book-tour-validation">
                {validationError}
              </p>
            )}
          </div>

          <div>
            <Label className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Note for the seller (optional)
            </Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={3}
              maxLength={500}
              className="mt-1 rounded-lg"
              placeholder="What you're looking for, questions you want to focus on…"
              data-testid="book-tour-message"
            />
            <p className="text-[11px] text-[#6B6B6B] mt-1.5">{message.length}/500</p>
          </div>

          <div className="rounded-md bg-[#FAFAFA] border border-[#E5E5E5] p-3 text-[12px] text-[#6B6B6B]">
            <strong className="text-ink">How payment works:</strong> the
            seller has 24h to accept or decline. If they accept, you'll
            be prompted to pay the $35 fee — the room only opens after
            payment. If they decline or don't respond, you pay nothing.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy || !!validationError}
            className="rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white disabled:opacity-50"
            data-testid="book-tour-submit"
          >
            {busy ? "Sending…" : "Send request"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
